<script lang="ts" setup>
import { ref, watch, computed } from 'vue'
import { useField } from 'vee-validate'
import type { FormField } from '../types/formTypeStructure'
import {
  useResponsiveSize,
  getLocalizedText
} from '../utils/formHelpers'
import { useFileBucketManager } from '../hooks/useFileBucketManager'
import FormFieldWrapper from '../FormFieldWrapper.vue'
import Button from '../../button/Button.vue'
import { AlertCircle, Trash2, ExternalLink, UploadCloud } from "lucide-vue-next";

interface Props {
  field: FormField & { fieldType: 'file' }
  name?: string
  currentLang?: string
  /**
   * Peut être une string (URL unique) ou un tableau (plusieurs fichiers déjà uploadés)
   */
  defaultValue?: string | string[]
  className?: string
}

const props = defineProps<Props>()

const size = computed(() => useResponsiveSize().calculateSize(window.innerWidth))
const fieldName = props.name ?? props.field.fieldName.toString()
const isMultiple = Array.isArray(props.field.response?.responseValue)
const label = computed(() => getLocalizedText(props.field.label, props.currentLang ?? 'fr'))
const isRequired = computed(() => props.field.validations?.some(v => v.validationType === 'required'))

// Émissions vers le FormEngine (pour mettre à jour formValues)
const emit = defineEmits<{
  (e: 'change', value: any): void
  (e: 'update:modelValue', value: any): void
}>()

const {
  value,
  errorMessage,
  handleChange,
  setTouched
} = useField<FileList | File | File[] | null>(fieldName, undefined, {
  initialValue: null
})

const selectedFiles = ref<File[]>([])
const uploadedFiles = ref<Array<{ url: string; filename?: string; originalName?: string }>>([])

// État pour le drag & drop et la référence d'input
const dragActive = ref(false)
const fileInputRef = ref<HTMLInputElement | null>(null)

// Utiliser useFileBucketManager si configuré
const fileBucketManager = computed(() =>
  props.field.fileToBucketManage
    ? useFileBucketManager(props.field.fileToBucketManage)
    : null
)

const hasUploadSupport = computed(() => fileBucketManager.value?.hasUploadSupport ?? false)
const hasDeleteSupport = computed(() => fileBucketManager.value?.hasDeleteSupport ?? false)
const isUploading = computed(() => fileBucketManager.value?.isUploading ?? false)
const isDeleting = computed(() => fileBucketManager.value?.isDeleting ?? false)
const bucketError = computed(() => fileBucketManager.value?.error ?? null)

watch(value, async (val) => {
  console.log('👀 [FileFormField] watch value triggered:', val)

  if (!val) {
    selectedFiles.value = []
    uploadedFiles.value = []
    return
  }

  // Gérer FileList / File[] / File correctement
  let filesArray: File[] = []
  if (val instanceof FileList) {
    filesArray = Array.from(val)
  } else if (Array.isArray(val)) {
    filesArray = val.filter((f): f is File => f instanceof File)
  } else if (val instanceof File) {
    filesArray = [val]
  }

  selectedFiles.value = filesArray
  console.log('📦 [FileFormField] fichiers sélectionnés:', filesArray.length)

  // Upload automatique si configuré
  const manager = fileBucketManager.value
  if (manager && hasUploadSupport.value && filesArray.length > 0) {
    manager.resetError()
    uploadedFiles.value = []

    for (const file of filesArray) {
      try {
        console.log('⬆️ [FileFormField] Upload de:', file.name)
        const result = await manager.uploadFile({
          file,
          payloadOverrides: {}
        })
        uploadedFiles.value.push({
          url: result.url,
          filename: result.filename,
          originalName: result.originalName
        })
        // Mettre à jour la valeur logique du champ avec les URL(s)
        const urls = uploadedFiles.value.map(f => f.url)
        const emittedValue = isMultiple ? urls : urls[0] ?? ''
        handleChange(emittedValue) // Mettre à jour value dans Vee-Validate
        emit('change', emittedValue)
        emit('update:modelValue', emittedValue)
        console.log('✅ [FileFormField] Upload réussi:', result.url)
      } catch (error) {
        console.error('❌ [FileFormField] Erreur upload:', error)
      }
    }
  }
})

const inputStyle = computed(() => ({
  width: `${size.value.width}px`,
  height: `${size.value.height}px`,
  borderRadius: `${size.value.borderRadius}px`,
  color: '#04406E',
  background: 'white',
  border: '1px solid #04406E'
}))

const baseClasses = 'transition-all focus:ring-2 focus:ring-[#04406E]'

const handleFilesFromInput = (files: FileList | null) => {
  if (!files || files.length === 0) {
    console.log('📁 [FileFormField] Aucun fichier sélectionné')
    return
  }

  console.log('📁 [FileFormField] Fichiers reçus:', files.length)

  // Important : passer FileList directement, pas Array.from()
  handleChange(files as any)
  setTouched(true)
}

const handleFileChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  const files = target.files
  console.log('📁 [FileFormField] handleFileChange déclenché', files)
  handleFilesFromInput(files)
}

const handleDrag = (e: DragEvent) => {
  e.preventDefault()
  e.stopPropagation()
  const isActive = e.type === 'dragenter' || e.type === 'dragover'
  dragActive.value = isActive
}

const handleDrop = (e: DragEvent) => {
  e.preventDefault()
  e.stopPropagation()
  dragActive.value = false
  if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
    console.log('📁 [FileFormField] handleDrop avec fichiers:', e.dataTransfer.files.length)
    handleFilesFromInput(e.dataTransfer.files)
  }
}

const openFileDialog = () => {
  console.log('📁 [FileFormField] openFileDialog appelé', fileInputRef.value)
  if (fileInputRef.value) {
    fileInputRef.value.click()
  }
}

const removeUploadedFile = async (index: number) => {
  console.log('🧹 [FileFormField] removeUploadedFile clicked', {
    index,
    uploadedFiles: uploadedFiles.value
  })

  const manager = fileBucketManager.value
  const uploaded = uploadedFiles.value[index]
  if (!uploaded) {
    console.warn('🧹 [FileFormField] Aucun fichier à cet index', index)
    return
  }

  // Nettoyer l'erreur précédente éventuelle
  manager?.resetError()

  // Si on a la configuration de suppression distante, on l'utilise
  if (manager && hasDeleteSupport.value && uploaded.url) {
    try {
      console.log('🧹 [FileFormField] Appel deleteFile avec URL', uploaded.url)
      await manager.deleteFile({
        // Suppression par URL, conforme à delete-file-by-url (file_url)
        fileUrl: uploaded.url
      })
      console.log('🧹 [FileFormField] Suppression distante OK')
    } catch (err) {
      console.error('❌ [FileFormField] Erreur lors de la suppression distante du fichier:', err)
      // On ne supprime pas localement si la suppression distante échoue
      return
    }
  } else {
    console.warn('🧹 [FileFormField] Pas de manager ou pas de support delete, suppression locale uniquement')
  }

  // Mettre à jour la liste locale et la valeur du champ uniquement si tout s'est bien passé
  uploadedFiles.value.splice(index, 1)

  const remainingUrls = uploadedFiles.value.map((f) => f.url)

  if (isMultiple) {
    // Pour plusieurs fichiers, on garde un tableau d'URLs ou vide
    const emitted = remainingUrls.length ? remainingUrls : []
    handleChange(emitted) // Mettre à jour value dans Vee-Validate
    emit('change', emitted)
    emit('update:modelValue', emitted)
  } else {
    // Pour un seul fichier, soit la première URL, soit une chaîne vide
    const emitted = remainingUrls[0] ?? ''
    handleChange(emitted) // Mettre à jour value dans Vee-Validate
    emit('change', emitted)
    emit('update:modelValue', emitted)
  }
  setTouched(true)
}

const openFile = (url: string) => {
  if (!url) return
  window.open(url, '_blank')
}
</script>

<template>
  <div class="file-field-container">
    <!-- Label dupliqué au-dessus de l'input file -->
    <div class="file-field-label-container">
      <span class="file-field-label-text">
        {{ label }}
        <AlertCircle
          v-if="isRequired"
          :size="16"
          class="inline text-red-500 ml-2"
        />
      </span>
    </div>

    <FormFieldWrapper :field="props.field" :current-lang="props.currentLang">
      <!-- Input caché AVANT la zone de drop -->
      <input
        ref="fileInputRef"
        type="file"
        :multiple="isMultiple"
        class="hidden"
        @change="handleFileChange"
      />

      <!-- Zone de drop principale -->
      <div
        :class="[
          'relative border-2 border-dashed rounded-lg cursor-pointer transition-all duration-300 ease-in-out',
          dragActive
            ? 'border-[#04406E] bg-[#e6f0f5] scale-[1.02]'
            : 'border-gray-300 hover:border-[#04406E] hover:bg-gray-50',
          props.className,
          isUploading && 'opacity-60'
        ]"
        @dragenter.prevent="handleDrag"
        @dragleave.prevent="handleDrag"
        @dragover.prevent="handleDrag"
        @drop.prevent="handleDrop"
        @click.stop="openFileDialog"
      >
        <div class="p-6 text-center">
          <div class="flex flex-col items-center space-y-4">
            <div
              :class="[
                'p-3 rounded-full transition',
                dragActive
                  ? 'bg-[#04406E] text-white'
                  : 'bg-gray-100 text-[#04406E] hover:bg-[#e6f0f5]'
              ]"
            >
              <UploadCloud :size="24" />
            </div>

            <div>
              <p class="text-base font-medium text-gray-700 mb-1">
                {{ dragActive
                  ? (currentLang === 'fr' ? 'Déposez vos fichiers ici' : 'Drop your files here')
                  : (currentLang === 'fr' ? 'Glissez-déposez vos fichiers' : 'Drag & drop your files') }}
              </p>
              <p class="text-sm text-gray-500">
                <span class="text-[#04406E] font-medium hover:underline">
                  {{ currentLang === 'fr' ? 'ou cliquez pour parcourir' : 'or click to browse' }}
                </span>
              </p>
              <p class="text-xs text-gray-400 mt-2">
                {{ currentLang === 'fr'
                  ? 'Vos fichiers seront envoyés de manière sécurisée vers le bucket.'
                  : 'Your files will be securely uploaded to the bucket.' }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Liste des fichiers uploadés -->
      <div v-if="uploadedFiles.length" class="mt-4 space-y-2">
        <div class="text-sm font-medium text-gray-700 mb-1">
          {{ currentLang === 'fr'
            ? `Fichier(s) (${uploadedFiles.length}) :`
            : `File(s) (${uploadedFiles.length}) :` }}
        </div>
        <div
          v-for="(uploaded, index) in uploadedFiles"
          :key="`uploaded-${index}`"
          class="flex flex-col items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow"
        >
          <div class="flex items-center space-x-3">
            <div class="text-lg">
              📎
            </div>
            <div class="flex-1 min-w-0">
              <p class="font-medium text-gray-800 truncate">
                {{ uploaded.filename || uploaded.originalName || uploaded.url }}
              </p>
              <p class="text-xs text-gray-500">
                {{ currentLang === 'fr' ? 'En ligne' : 'Online' }}
              </p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <Button
              type="button"
              variant="validation"
              class-name="!px-2 !py-2 hover:!bg-gray-100 !bg-transparent !border-none"
              @click.stop="() => openFile(uploaded.url)"
            >
              <ExternalLink class="w-4 h-4 text-[#04406E]" />
            </Button>
            <Button
              type="button"
              variant="validation"
              class-name="!px-2 !py-2 hover:!bg-red-50 !bg-transparent !border-none"
              @click.stop="removeUploadedFile(index)"
            >
              <Trash2 class="w-4 h-4 text-red-500" />
            </Button>
          </div>
        </div>
      </div>

      <p v-if="bucketError && !isUploading" class="text-red-500 mt-2 text-sm">
        Erreur: {{ bucketError }}
      </p>
      <p v-if="errorMessage" class="text-sm text-red-500 mt-1">{{ errorMessage }}</p>
    </FormFieldWrapper>
  </div>
</template>

<style scoped>
.file-field-container {
  position: relative;
  display: block;
  width: 100%;
}

.file-field-label-container {
  position: relative;
  display: block;
  margin-bottom: 0.75rem;
  z-index: 100;
}

.file-field-label-text {
  display: block;
  font-size: 1.125rem;
  font-weight: 700;
  color: #333333;
  line-height: 1.2;
  margin: 0;
  padding: 0;
  position: static !important;
  transform: none !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
  bottom: auto !important;
  height: auto !important;
  overflow: visible !important;
  text-align: left !important;
  text-overflow: unset !important;
  white-space: normal !important;
  pointer-events: auto !important;
  border: none !important;
  transform-origin: unset !important;
  transition: none !important;
}

input[type="file"]::file-selector-button {
  background: #04406E;
  color: #fff;
  border: none;
  border-radius: 9999px;
  padding: 0.5rem 1.25rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

input[type="file"]::file-selector-button:hover {
  background: #033a5a;
}
</style>
